import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ProgramForm, ProgramFormsFilters, SectionFormRelation, TrainingProgramForm } from '../../types';
import { TeamMember } from '../../../../../types';
import { ToolTip } from '../../components/ToolTip';
import ProgramFormRow from './ProgramFormRow';
import { getFullNameOrEmail } from '../../../../../redux/user';
import { ArrowDropDown } from '@mui/icons-material';

type Order = 'asc' | 'desc';

const columnStyles = { borderBottom: 'unset', minWidth: '70px', maxWidth: '70px', width: '70px', padding: 0, margin: 0 }

const CollapsibleTable = (props: {
  rows: SectionFormRelation[],
  formsIndex: {[Identfier: number]: TrainingProgramForm[]};
  setFileUploadModalIsOpen: (open: boolean) => void;
  setSelectedForm: (id: number) => void;
  setSelectedTrainingForm: (id: number) => void;
  teamMembersById: Map<number, TeamMember>;
  handleDeleteClick: (id: number) => void;
  handleAddForm: (id: number) => void;
  handleEditTeamMember: (form: ProgramForm, formTeamMembers: TeamMember[]) => void;
  filters: ProgramFormsFilters | null;
  searchValue: string;
  filterValuesSet: boolean;
  programId?: number;
  sectionId: number;
}) => {
  const { rows, filters, searchValue, teamMembersById, programId, sectionId } = props;
  const [sortDirection, setSortDirection] = useState<Order>('desc');
  const [sortBy, setSortBy] = useState<string>('formName');
  const [colWidths, setColWidths] = useState<Array<number>>([]);
  const ref: React.MutableRefObject<any> = useRef(null);

  const sortTableData = (column: string) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortBy(column);
  }

  useEffect(() => {
    setColWidths(
      Array.from(ref?.current?.children || []).map((c : any) => {
        return c?.getBoundingClientRect()?.width
      })
    );
  }, [ref]);

  const sortByUser = (field: Extract<keyof TrainingProgramForm, 'uploaded_by' | 'user_id'>) => {
    return rows.map(row => {
      return {
        ...row,
        training_program_forms: (row.training_program_forms ?? []).sort((a, b) => {
          const ids = {
            a: Number(a[field]),
            b: Number(b[field]),
          }

          const users = {
            a: teamMembersById.get(ids.a),
            b: teamMembersById.get(ids.b),
          }

          const names = {
            a: ids.a ? getFullNameOrEmail(users.a).toLowerCase() : '',
            b: ids.b ? getFullNameOrEmail(users.b).toLowerCase() : '',
          }

          const modifier = sortDirection === 'asc' ? 1 : -1

          return names.a.localeCompare(names.b) * modifier
        })
      }
    }).sort((a, b) => {
      const forms = {
        a: a.training_program_forms.length ? a.training_program_forms[0] : null,
        b: b.training_program_forms.length ? b.training_program_forms[0] : null,
      }

      const ids = {
        a: Number(forms.a && forms.a[field]),
        b: Number(forms.b && forms.b[field]),
      }

      const users = {
        a: teamMembersById.get(ids.a),
        b: teamMembersById.get(ids.b),
      }

      const names = {
        a: ids.a ? getFullNameOrEmail(users.a).toLowerCase() : '',
        b: ids.b ? getFullNameOrEmail(users.b).toLowerCase() : '',
      }

      const modifier = sortDirection === 'asc' ? 1 : -1

      return names.a.localeCompare(names.b) * modifier
    })
  }

  const sortByUploadedForm = () => {
    return rows.map(row => {
      return {
        ...row,
        training_program_forms: (row.training_program_forms ?? []).sort((a, b) => {
          const uploads = {
            a: a.file_api_uploads?.display_name?.toLowerCase() ?? '',
            b: b.file_api_uploads?.display_name?.toLowerCase() ?? '',
          }

          const modifier = sortDirection === 'asc' ? 1 : -1

          return uploads.a.localeCompare(uploads.b) * modifier
        })
      }
    }).sort((a, b) => {
      const forms = {
        a: a.training_program_forms.length ? a.training_program_forms[0] : null,
        b: b.training_program_forms.length ? b.training_program_forms[0] : null,
      }

      const uploads = {
        a: forms.a?.file_api_uploads,
        b: forms.b?.file_api_uploads
      }

      const names = {
        a: uploads.a?.display_name.toLowerCase() ?? '',
        b: uploads.b?.display_name?.toLowerCase() ?? '',
      }

      const modifier = sortDirection === 'asc' ? 1 : -1

      return names.a.localeCompare(names.b) * modifier
    })
  }

  const sortByFormName = () => {
    return rows.sort((a, b) => {
      const modifier = sortDirection === 'asc' ? 1 : -1
      return a.form.name.localeCompare(b.form.name) * modifier
    })
  }

  const sortedForms = useMemo(() => {
    switch(sortBy) {
      case 'formName':
        return sortByFormName();
      case 'uploadedBy':
        return sortByUser('uploaded_by');
      case 'teamMember':
        return sortByUser('user_id');
      case 'uploadedForm':
        return sortByUploadedForm();
      default:
        return sortByFormName();
    }
  }, [rows, sortDirection]);

  return (
    <Table size="small" sx={{ tableLayout: 'fixed' }}>
      <TableHead sx={{ display: "table-header-group" }}>
        <TableRow sx={{ borderBottom: 'unset'}} ref={ref}>
          <TableCell
            sx={{ ...columnStyles }}
            sortDirection={sortDirection}
          >
            <TableSortLabel
              active
              direction={sortBy === 'formName' ? sortDirection : 'desc'}
              onClick={() => sortTableData('formName')}
              IconComponent={ArrowDropDown}
            >
              Form Name
            </TableSortLabel>
          </TableCell>
          <TableCell sx={{ ...columnStyles, minWidth: '30px', maxWidth: '30px', width: '30px' }} align="center">Upload</TableCell>
          <TableCell sx={columnStyles} align="center">
            <TableSortLabel
              active
              direction={sortBy === 'uploadedBy' ? sortDirection : 'desc'}
              onClick={() => sortTableData('uploadedBy')}
              IconComponent={ArrowDropDown}
            >
              Uploaded By
            </TableSortLabel>
          </TableCell>
          <TableCell sx={columnStyles} align="center">
            <TableSortLabel
              active
              direction={sortBy === 'teamMember' ? sortDirection : 'desc'}
              onClick={() => sortTableData('teamMember')}
              IconComponent={ArrowDropDown}
            >
              Team Member
            </TableSortLabel>
          </TableCell>
          <TableCell sx={{ ...columnStyles,  minWidth: '70px', maxWidth: '70px', width: '70px' }} align="center">Status</TableCell>
          <TableCell sx={{ ...columnStyles, width: '150px' }} align="center">
            <TableSortLabel
              direction={sortBy === 'uploadedForm' ? sortDirection : 'desc'}
              active
              onClick={() => sortTableData('uploadedForm')}
              IconComponent={ArrowDropDown}
            >
              Uploaded Form
            </TableSortLabel>
            <ToolTip icon="info" helpText="Only users with Admin and Owner level permissions can delete files."/>
          </TableCell>
          <TableCell sx={{ ...columnStyles, minWidth: '40px', maxWidth: '40px', width: '40px', marginRight: '5px' }} align="center">
            <span
              style={{
                verticalAlign: 'bottom',
                marginRight: '5px'
              }}
            >
              Exempt
            </span>
            <ToolTip
              icon="info"
              helpText="If your practice doesn't require this form, please check the box so that the compliance doesn't get affected. Only users with Admin and Owner level permissions can exempt files."
            />
          </TableCell>
          <TableCell sx={{ ...columnStyles, minWidth: '20px', maxWidth: '20px', width: '20px' }} align="center" />
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedForms
          .map(row => {
            return (
              <ProgramFormRow
                {...props}
                sectionFormRelation={row}
                key={row.id}
                colWidths={colWidths}
                filters={filters}
                searchValue={searchValue}
                programId={programId}
                sectionId={sectionId}
              />
            )
          }
        )}
      </TableBody>
    </Table>
  );
}

export default CollapsibleTable;
